import { useMemo, useRef } from 'react';

import cs from 'classnames';

// import { formatFileSize } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import ProgressBar from 'components/shared/ProgressBar/ProgressBar';

export default function S3Uploader({
  maxFiles = 1,
  formats = '.zip',
  title = 'Upload file',
  showButtons = false,
  onUpload,
  onCancel = () => {},
  files = [],
  setFiles = () => {},
  uploadProgressList = []
}) {
  const fileInputRef = useRef(null);
  const allowedFormats = useMemo(() => {
    return formats;
  }, [formats]);
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length > 0) {
      if (files.length + newFiles.length > maxFiles) {
        showAlert({
          color: 'danger',
          message: `Maximum ${maxFiles} files allowed`
        });
        return;
      }
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };
  const handleOpenPicker = () => {
    fileInputRef.current.click();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      if (files.length + droppedFiles.length > maxFiles) {
        showAlert({
          color: 'danger',
          message: `Maximum ${maxFiles} files allowed`
        });
        return;
      }
      setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const description = `Upload files in the following formats: ${formats}`;
  return (
    <>
      {showButtons && (
        <div className="flex justify-center gap-4">
          <Button onClick={onCancel} outlined text="Cancel" className="mt-4" />
          <Button
            disabled={files.length === 0}
            onClick={() => onUpload(files)}
            text="Start uploading"
            className="mt-4"
          />
        </div>
      )}
      <div>
        <div
          className={cs(
            'border-gray-400 flex h-[200px] w-full cursor-pointer flex-col items-center gap-5 rounded-lg border border-dashed p-8',
            'hover:border-primary-500 hover:bg-primary-25'
          )}
          onClick={handleOpenPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}>
          <Icon icon="cloud-upload" />
          <div className="flex flex-col items-center justify-center gap-1.5 p-0">
            <span className="text-gray-900 text-sm font-medium">{title}</span>
            <span className="text-gray-600 text-xs font-normal">{description}</span>
            {maxFiles && (
              <span className="text-gray-600 text-xs font-normal">{`Maximum ${maxFiles} files allowed`}</span>
            )}
          </div>
          <Button variant="tertiary" icon="export-mono" text="Upload" color="neutral" />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple={maxFiles > 1}
          onChange={handleFileChange}
          accept={allowedFormats}
        />
        {files.length > 0 && (
          <ul className="mt-4 flex flex-col gap-4">
            {files.map((f, index) => (
              <li
                key={index}
                className="flex flex-col gap-2 rounded-xl border border-solid border-neutral-200 p-4">
                <div className="items-top flex justify-between">
                  <div className="flex items-center gap-2">
                    <Icon icon="file-zip" />
                    <div className="flex flex-col gap-2">
                      <span className="font-semibold">{f.name}</span>
                      {/* TODO: format file size to be human readable */}
                      <span className="text-xs">{f.size}</span>
                    </div>
                  </div>
                  <div className="h-fit">
                    <Icon
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                      icon="close-circle-filled"
                      color="danger"
                    />
                  </div>
                </div>
                {uploadProgressList.find((fp) => fp.filename === f.name) && (
                  <ProgressBar
                    progress={uploadProgressList.find((fp) => fp.filename === f.name).progress}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
